import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useNotification } from "~hooks/useNotification.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";

export const useDeleteLeague = () => {
  const { sport, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const request = useApi();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();

  return useMutation<DefaultOperationSuccessResponse, Error, string>({
    mutationFn: async (leagueId: string) => {
      const url = `${sport}/o/${organizationId}/leagues/${leagueId}`;
      return await request(url, {
        method: "DELETE",
      });
    },
    onSuccess: async (_, leagueId) => {
      queryClient.setQueryData(["permission-scope-current-user", sport, organizationId], (oldScope) => {
        if (!oldScope) {
          return;
        }

        const updatedScope = JSON.parse(JSON.stringify(oldScope));
        const leagueToDelete = updatedScope.resources?.leagues?.[leagueId];

        if (leagueToDelete) {
          updatedScope.scope.leagueTree = updatedScope.scope.leagueTree.filter(({ leagueId: id }) => id !== leagueId);
        }

        return updatedScope;
      });

      notification.success({
        message: formatMessage({ id: "league.delete.success" }),
        placement: "bottomRight",
      });

      await invalidateCache();
    },
  });
};
